import styles from './index.module.css'

import { LuArrowDown } from '@react-icons/all-files/lu/LuArrowDown'
import { LuArrowUp } from '@react-icons/all-files/lu/LuArrowUp'
import { LuArrowUpDown } from '@react-icons/all-files/lu/LuArrowUpDown'
import { LuAudioLines } from '@react-icons/all-files/lu/LuAudioLines'
import { LuCalendar } from '@react-icons/all-files/lu/LuCalendar'
import { LuChevronLeft } from '@react-icons/all-files/lu/LuChevronLeft'
import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import { LuDownload } from '@react-icons/all-files/lu/LuDownload'
import { LuEye } from '@react-icons/all-files/lu/LuEye'
import { LuMusic } from '@react-icons/all-files/lu/LuMusic'
import { LuSettings } from '@react-icons/all-files/lu/LuSettings'
import cn from 'clsx'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { LICENSE_REQUEST } from '@disco/constants/FeatureTypes'
import type { LicenseRequestSubmissionTrack } from '@disco/state-management/api/license-requests'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import {
  getConstanceSettings,
  getCustomFieldsConfig,
} from '@disco/state-management/selectors/user'
import { getInitials } from '@disco/utils/string-utils'

import type { ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'

import DateRangePicker from 'components/common/date-range-picker'
import DocumentHeadTags from 'components/document-head-tags'
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  IconButton,
  Popover,
  SearchTextField,
  Separator,
  Switch,
  Table,
  Text,
} from 'components/ds'
import InlineLink from 'components/ui/inline-link'

import { useSelector } from 'hooks/use-redux'

import { useDebouncedLibraryToggle } from '../use-debounced-library-toggle'
import { formatDate } from './format-date'
import { SubmissionDetailPanel } from './submission-detail-panel'
import type { DateRange } from './use-license-request-submissions'
import { useLicenseRequestSubmissions } from './use-license-request-submissions'
import { DEFAULT_EXPORT_FIELDS } from './default-fields'

type SortColumn = 'id' | 'created'

function getSortIcon(column: SortColumn, ordering: string) {
  const isAsc = ordering === column
  const isDesc = ordering === `-${column}`
  if (!isAsc && !isDesc) return <LuArrowUpDown size={12} />
  return isAsc ? <LuArrowUp size={12} /> : <LuArrowDown size={12} />
}

function TrackRow({ track }: { track: LicenseRequestSubmissionTrack }) {
  return (
    <Flex gap="2" align="center">
      {track.artwork?.artwork_sizes['160x160'] ? (
        <img
          src={track.artwork.artwork_sizes['160x160']}
          alt=""
          className={styles.trackArtwork}
        />
      ) : (
        <Flex
          align="center"
          justify="center"
          className={styles.trackArtworkPlaceholder}
        >
          <LuAudioLines size={16} />
        </Flex>
      )}
      <Flex direction="column" gap="1">
        <Text size="2" weight="medium">
          {track.name}
        </Text>
        <Text size="2" color="gray">
          {track.artist}
        </Text>
      </Flex>
    </Flex>
  )
}

function TrackList({ tracks }: { tracks: LicenseRequestSubmissionTrack[] }) {
  const [expanded, setExpanded] = useState(false)

  if (tracks.length === 0) {
    return null
  }

  const extraCount = tracks.length - 1

  return (
    <Flex direction="column" gap="2" className={styles.trackListContent}>
      <Flex align="center" justify="between" gap="2">
        <TrackRow track={tracks[0]!} />
        {extraCount > 0 && (
          <button
            type="button"
            className={styles.trackMoreButton}
            onClick={() => setExpanded(e => !e)}
          >
            +{extraCount}
          </button>
        )}
      </Flex>
      {expanded &&
        tracks.slice(1).map(track => <TrackRow key={track.id} track={track} />)}
    </Flex>
  )
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <Flex direction="column" align="center" justify="center" gap="3" py="9">
      {!filtered && (
        <Flex align="center" justify="center" className={styles.emptyStateIcon}>
          <LuMusic size={16} />
        </Flex>
      )}
      <Flex
        direction="column"
        align="center"
        gap="2"
        className={styles.emptyStateText}
      >
        <Text size="2" weight={filtered ? 'regular' : 'bold'}>
          {filtered ? 'No results found' : 'No license requests yet'}
        </Text>
        {!filtered && (
          <Text size="1" color="gray" align="center">
            License requests submitted for this catalog will appear here
          </Text>
        )}
      </Flex>
    </Flex>
  )
}

const ALL_TIME_START = new Date(0)

const LibrariesLibraryLicenseRequests = () => {
  const params = useParams<{ libraryId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const licenseRequestEnabled = useFeatureEnabled(LICENSE_REQUEST)

  const constance = useSelector(getConstanceSettings)
  const extraMetadata = useSelector(getCustomFieldsConfig)
  const licenseRequestListingEnabled = constance.LICENSE_REQUESTS_LISTING

  const library = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )

  const [ordering, setOrdering] = useState('-created')
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [dateRange, setDateRange] = useState<DateRange | null>(null)
  const [pickerKey, setPickerKey] = useState(0)
  const [downloadConfigOpen, setDownloadConfigOpen] = useState(false)
  const [exportFieldSearch, setExportFieldSearch] = useState('')
  const [selectedExportFields, setSelectedExportFields] = useState<string[]>(
    DEFAULT_EXPORT_FIELDS,
  )
  const [selectedWriterFields, setSelectedWriterFields] = useState<string[]>([])

  const handleDateRangeChange = (start: Date | null, end?: Date | null) => {
    if (!start) {
      setDateRange(null)
      return
    }

    const isRepeatOfSelectedDay =
      dateRange?.from.getTime() === dateRange?.to.getTime() &&
      dateRange?.from.getTime() === start.getTime() &&
      (!end || end.getTime() === start.getTime())
    if (isRepeatOfSelectedDay) {
      setDateRange(null)
      return
    }

    setDateRange({ from: start, to: end ?? start })
  }

  const handleClearDateRange = () => {
    setDateRange(null)
    setPickerKey(key => key + 1)
  }

  const toggleExportField = (field: string, checked: boolean) => {
    setSelectedExportFields(selectedFields =>
      checked
        ? [...selectedFields, field]
        : selectedFields.filter(selectedField => selectedField !== field),
    )
  }

  const toggleWriterField = (field: string, checked: boolean) => {
    setSelectedWriterFields(selectedWriterFields =>
      checked
        ? [...selectedWriterFields, field]
        : selectedWriterFields.filter(s => s !== field),
    )
  }

  const {
    submissions,
    total,
    start,
    end,
    hasNext,
    hasPrev,
    goNext,
    goPrev,
    isLoading,
    exportUrl,
  } = useLicenseRequestSubmissions(
    libraryId,
    ordering,
    selectedExportFields,
    selectedWriterFields,
    dateRange,
  )

  const toggleOrdering = (column: SortColumn) => {
    if (ordering === `-${column}`) {
      setOrdering(column)
    } else {
      setOrdering(`-${column}`)
    }
  }

  const [enableLicenseRequest, toggleEnableLicenseRequest] =
    useDebouncedLibraryToggle(
      libraryId,
      'enable_license_request',
      Boolean(library?.enable_license_request),
    )

  const handleToggle = (checked: boolean) => {
    mixpanel.sendPureEvent('License request settings changed', {
      enable: checked,
    })
    toggleEnableLicenseRequest(checked)
  }

  const availableExportFields = DEFAULT_EXPORT_FIELDS.concat(
    Object.values(extraMetadata).map(m => m.text),
  )
  // Writer fields show as 'Writer <field_name>' in the UI
  // to help disambiguate, but the Writer part is stripped out by the
  // backend.
  const availableWriterFields = [
    'Writer Name',
    'Writer Percentage',
    'Writer Publisher',
    'Writer PRO',
  ]
  const exportDateRangeLabel = dateRange
    ? `${formatDate(dateRange.from)} to ${formatDate(dateRange.to)}`
    : 'All time'

  const normalizedExportFieldSearch = exportFieldSearch.trim().toLowerCase()
  const filteredExportFields = availableExportFields.filter(field =>
    field.toLowerCase().includes(normalizedExportFieldSearch),
  )
  const filteredExportWriterFields = availableWriterFields.filter(field =>
    field.toLowerCase().includes(normalizedExportFieldSearch),
  )

  const allExportFieldsFiltered = filteredExportFields.concat(
    filteredExportWriterFields,
  )

  const handleDownloadCSV = () => {
    mixpanel.sendPureEvent('License request submissions exported', {
      ordering,
      hasDateFilter: Boolean(dateRange),
    })
  }

  const exportDisabled = (isLoading && submissions.length === 0) || total === 0

  if (!(licenseRequestListingEnabled && licenseRequestEnabled)) {
    return <Text>License requests feature currently not enabled</Text>
  }
  const selectedSubmission =
    selectedIndex != null ? submissions[selectedIndex] ?? null : null

  const downloadCSVButtonContent = (
    <>
      <LuDownload size={14} />
      <span>Download CSV</span>
    </>
  )

  return (
    <>
      {library && !library['loading'] && (
        <DocumentHeadTags
          title={`${library['name']} | License requests | DISCO`}
        />
      )}
      <Box p="4" mb="5" className={styles.tableCard}>
        <Flex justify="between" align="center">
          <h1 className="Heading Heading--500">License request form</h1>
        </Flex>
        <Text size="2" color="gray" as="p" mb="3" mt="2">
          Enable a license request form to let visitors submit licensing
          inquiries directly from your Catalog. License requests will be emailed
          to recipients configured{' '}
          <InlineLink to={`/catalogs/${libraryId}/notifications`}>
            here
          </InlineLink>
          .
        </Text>
        <Flex gap="2" align="center">
          <Switch
            disabled={Boolean(library?.loading)}
            checked={enableLicenseRequest}
            onCheckedChange={handleToggle}
          />
          <Text size="2">Enable license request form</Text>
        </Flex>
      </Box>

      <Flex mb="3" justify="end">
        {exportDisabled ? (
          <Button variant="soft" color="gray" size="2" disabled>
            {downloadCSVButtonContent}
          </Button>
        ) : (
          <Flex gap="2">
            <Button variant="soft" color="gray" size="2" asChild>
              <a href={exportUrl} download onClick={handleDownloadCSV}>
                {downloadCSVButtonContent}
              </a>
            </Button>
            <Popover.Root
              open={downloadConfigOpen}
              onOpenChange={setDownloadConfigOpen}
            >
              <Popover.Trigger>
                <Button
                  variant="soft"
                  color="gray"
                  size="2"
                  aria-label="Configure CSV export"
                >
                  <LuSettings size={14} />
                </Button>
              </Popover.Trigger>
              <Popover.Content
                sideOffset={5}
                collisionPadding={32}
                width="480px"
                className={styles.exportPopover}
              >
                <Flex
                  direction="column"
                  gap="3"
                  className={styles.exportPopoverBody}
                >
                  <Text size="4" weight="medium">
                    Export Options
                  </Text>
                  <Text size="2" weight="medium">
                    Selected time range: {exportDateRangeLabel}
                  </Text>
                  <Text size="3">Metadata Fields</Text>
                  <SearchTextField
                    value={exportFieldSearch}
                    onChange={event => setExportFieldSearch(event.target.value)}
                    placeholder="Search metadata fields..."
                    aria-label="Search metadata fields"
                  />
                  {allExportFieldsFiltered.length > 0 ? (
                    <Flex direction="column" className={styles.exportFieldList}>
                      <Grid columns="3" gap="2">
                        {filteredExportFields.map(field => (
                          <Text
                            truncate
                            title={field}
                            as="label"
                            size="2"
                            key={field}
                          >
                            <Flex gap="2" align="center">
                              <Checkbox
                                checked={selectedExportFields.includes(field)}
                                onCheckedChange={checked =>
                                  toggleExportField(field, checked === true)
                                }
                              />
                              {field}
                            </Flex>
                          </Text>
                        ))}
                        {filteredExportWriterFields.map(field => (
                          <Text
                            truncate
                            title={field}
                            as="label"
                            size="2"
                            key={field}
                          >
                            <Flex gap="2" align="center">
                              <Checkbox
                                checked={selectedWriterFields.includes(field)}
                                onCheckedChange={checked =>
                                  toggleWriterField(field, checked === true)
                                }
                              />
                              {field}
                            </Flex>
                          </Text>
                        ))}
                      </Grid>
                    </Flex>
                  ) : (
                    <Text size="2" color="gray">
                      No metadata fields found
                    </Text>
                  )}
                </Flex>
                <Flex
                  direction="row"
                  gap="2"
                  pt="4"
                  justify="end"
                  flexShrink="0"
                >
                  <Button
                    variant="soft"
                    color="gray"
                    size="2"
                    onClick={() => setDownloadConfigOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button variant="solid" size="2" asChild>
                    <a href={exportUrl} download onClick={handleDownloadCSV}>
                      Download
                    </a>
                  </Button>
                </Flex>
              </Popover.Content>
            </Popover.Root>
          </Flex>
        )}
      </Flex>

      <div className={styles.tableCard}>
        <div className={styles.toolbar}>
          <Flex gap="3" align="center">
            <DateRangePicker
              key={pickerKey}
              theme="default"
              showPredefinedRanges
              applyButton={false}
              closeOnRangeSelect
              dropdownClassName={cn(
                styles.dateRangeDropdown,
                !dateRange && styles.noSelectionHighlight,
              )}
              minDate={ALL_TIME_START}
              maxDate={new Date()}
              startDate={dateRange?.from ?? new Date()}
              endDate={dateRange?.to ?? new Date()}
              onChange={handleDateRangeChange}
              disabled={isLoading && submissions.length === 0}
              trigger={
                <Button
                  variant="surface"
                  color="gray"
                  size="2"
                  className={styles.datePicker}
                  disabled={isLoading && submissions.length === 0}
                >
                  <LuCalendar size={14} />
                  <span>
                    {dateRange
                      ? `${formatDate(dateRange.from)} to ${formatDate(
                          dateRange.to,
                        )}`
                      : 'Select date range'}
                  </span>
                </Button>
              }
            />
            {dateRange && (
              <Button
                variant="ghost"
                color="gray"
                size="1"
                onClick={handleClearDateRange}
              >
                Clear
              </Button>
            )}
          </Flex>
          <Flex gap="2" align="center">
            {total > 0 && (
              <Text size="2" color="gray">
                {start} – {end} of {total}
              </Text>
            )}
            <Flex gap="1">
              <IconButton
                variant="soft"
                size="1"
                disabled={!hasPrev || isLoading}
                onClick={goPrev}
                aria-label="Previous page"
              >
                <LuChevronLeft size={14} />
              </IconButton>
              <IconButton
                variant="soft"
                size="1"
                disabled={!hasNext || isLoading}
                onClick={goNext}
                aria-label="Next page"
              >
                <LuChevronRight size={14} />
              </IconButton>
            </Flex>
          </Flex>
        </div>

        <div className={styles.tableClip}>
          <Separator size="4" />
          <Table.Root variant="ghost">
            <Table.Header>
              <Table.Row className={styles.tableHeader}>
                <Table.ColumnHeaderCell>
                  <button
                    type="button"
                    className={styles.sortableHeader}
                    onClick={() => toggleOrdering('id')}
                  >
                    ID #{getSortIcon('id', ordering)}
                  </button>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  <button
                    type="button"
                    className={styles.sortableHeader}
                    onClick={() => toggleOrdering('created')}
                  >
                    Submission Date
                    {getSortIcon('created', ordering)}
                  </button>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Track Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>View</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading && submissions.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Text size="2" color="gray">
                      Loading…
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
              {!isLoading && submissions.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <EmptyState filtered={Boolean(dateRange)} />
                  </Table.Cell>
                </Table.Row>
              )}
              {submissions.map((submission, rowIndex) => {
                const initials = getInitials(submission.answers.user_name ?? '')

                return (
                  <Table.Row key={submission.id}>
                    <Table.Cell>
                      <Text size="2" color="gray">
                        {submission.id}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Box className={styles.nameContent}>
                        <Flex gap="2" align="center">
                          <Avatar
                            size="1"
                            fallback={initials || '?'}
                            radius="full"
                            className={styles.avatar}
                          />
                          <Text size="2" truncate>
                            {submission.answers.user_name}
                          </Text>
                        </Flex>
                      </Box>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2" color="gray">
                        {submission.answers.user_email}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2" color="gray">
                        {formatDate(submission.created)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <TrackList tracks={submission.tracks} />
                    </Table.Cell>
                    <Table.Cell>
                      <Flex align="start" pt="3">
                        <IconButton
                          variant="ghost"
                          size="1"
                          onClick={() => setSelectedIndex(rowIndex)}
                          aria-label="View submission"
                        >
                          <LuEye size={16} />
                        </IconButton>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        </div>
      </div>

      <SubmissionDetailPanel
        open={selectedIndex != null}
        submission={selectedSubmission}
        onClose={() => setSelectedIndex(null)}
        onPrev={() => setSelectedIndex(i => (i != null ? i - 1 : null))}
        onNext={() => setSelectedIndex(i => (i != null ? i + 1 : null))}
        prevDisabled={selectedIndex === 0}
        nextDisabled={selectedIndex === submissions.length - 1}
      />
    </>
  )
}

export default LibrariesLibraryLicenseRequests
